import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Car, KeyRound } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user === undefined) return null;
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/admin");
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-10 h-10 rounded-md bg-black text-white grid place-items-center">
            <Car size={20} />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Fair Rideshare</h1>
            <p className="text-xs text-gray-500 tracking-widest uppercase">CRM &amp; Fleet Manager</p>
          </div>
        </div>
        <Card className="border-gray-200 shadow-sm">
          <CardContent className="p-8">
            <h2 className="font-heading text-xl font-semibold mb-1">Sign in to your account</h2>
            <p className="text-sm text-gray-500 mb-6">Use your staff or admin credentials</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5"
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5"
                  required
                />
              </div>
              {err && (
                <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2.5">
                  {err}
                </div>
              )}
              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="w-full bg-black hover:bg-gray-800 text-white"
              >
                <KeyRound size={16} className="mr-2" />
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
